/**
 * Service Ticket Service - Enterprise-Grade Ticket Management with Tenant Isolation
 * 
 * This service provides comprehensive service ticket (maintenance work order) management
 * with strict tenant isolation. All operations are scoped to the company/tenant context.
 * 
 * @module serviceTicketService
 * @category Operations
 * @security CRITICAL - Enforces multi-tenant data isolation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  QueryConstraint,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';
import type { Vendor } from '../../types/vendor';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type TicketPriority = 'critical' | 'high' | 'medium' | 'low';
export type TicketStatus = 'open' | 'assigned' | 'in_progress' | 'waiting_parts' | 'resolved' | 'closed' | 'cancelled';
export type TicketType = 'equipment_failure' | 'preventive_maintenance' | 'inspection' | 'repair' | 'installation' | 'emergency' | 'safety_issue' | 'other';
export type ReviewStatus = 'pending' | 'rated' | 'auto_rated' | 'skipped';

export interface TicketRating {
  score: number; // 1-5
  categories?: {
    cleanliness?: number;
    timeliness?: number;
    quality?: number;
    safety?: number;
  };
  tags: string[]; // e.g. 'Late', 'No PPE', 'Great Service'
  comment?: string;
  ratedBy: string;
  ratedAt: Date;
  isAutoRated: boolean;
}

export interface ServiceTicket {
  id: string;
  companyId: string;
  ticketNumber: string;
  title: string;
  description: string;
  type: TicketType;
  priority: TicketPriority;
  status: TicketStatus;
  wellId?: string;
  wellName?: string;
  equipmentId?: string;
  equipmentName?: string;
  vendorId?: string;
  vendorName?: string;
  createdBy: string;
  createdByName?: string;
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  scheduledDate?: Date;
  startedAt?: Date;
  completedAt?: Date;
  closedAt?: Date;
  workPerformed?: string;
  rootCause?: string;
  correctiveAction?: string;
  partsUsed?: Array<{
    partId: string;
    partName: string;
    quantity: number;
    unitCost?: number;
  }>;
  safetyHazards?: string[];
  ppeRequired?: string[];
  permitsRequired?: string[];
  lockoutTagout?: boolean;
  estimatedCost?: number;
  actualCost?: number;
  laborHours?: number;
  laborCost?: number;
  partsCost?: number;
  responseDeadline?: Date;
  resolutionDeadline?: Date;
  slaBreached?: boolean;
  photos?: string[];
  documents?: string[];
  notes?: string;
  internalNotes?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  publicLinkActive?: boolean;
  publicLink?: string;
  reviewStatus?: ReviewStatus;
  review?: TicketRating;
}

export interface CreateTicketData {
  title: string;
  description: string;
  type: TicketType;
  priority: TicketPriority;
  wellId?: string;
  wellName?: string;
  equipmentId?: string;
  equipmentName?: string;
  vendorId?: string;
  vendorName?: string;
  assignedTo?: string;
  assignedToName?: string;
  scheduledDate?: Date;
  estimatedCost?: number;
  safetyHazards?: string[];
  ppeRequired?: string[];
  permitsRequired?: string[];
  lockoutTagout?: boolean;
  tags?: string[];
  notes?: string;
  internalNotes?: string;
  customFields?: Record<string, any>;
}

export interface UpdateTicketData {
  title?: string;
  description?: string;
  type?: TicketType;
  priority?: TicketPriority;
  status?: TicketStatus;
  assignedTo?: string;
  assignedToName?: string;
  vendorId?: string;
  vendorName?: string;
  scheduledDate?: Date;
  startedAt?: Date;
  completedAt?: Date;
  workPerformed?: string;
  rootCause?: string;
  correctiveAction?: string;
  partsUsed?: Array<{
    partId: string;
    partName: string;
    quantity: number;
    unitCost?: number;
  }>;
  actualCost?: number;
  laborHours?: number;
  laborCost?: number;
  partsCost?: number;
  notes?: string;
  internalNotes?: string;
  tags?: string[];
  customFields?: Record<string, any>;
}

export interface TicketListOptions {
  status?: TicketStatus | TicketStatus[];
  priority?: TicketPriority | TicketPriority[];
  type?: TicketType;
  wellId?: string;
  assignedTo?: string;
  createdBy?: string;
  vendorId?: string;
  limit?: number;
  orderByField?: 'createdAt' | 'updatedAt' | 'priority' | 'scheduledDate' | 'ticketNumber';
  orderDirection?: 'asc' | 'desc';
  includeCompleted?: boolean;
}

// ============================================================================
// ERROR CLASSES
// ============================================================================

export class TicketServiceError extends Error {
  constructor(message: string, public code: string, public details?: any) {
    super(message);
    this.name = 'TicketServiceError';
  }
}

export class TicketNotFoundError extends TicketServiceError {
  constructor(ticketId: string) {
    super(`Ticket not found: ${ticketId}`, 'TICKET_NOT_FOUND', { ticketId });
    this.name = 'TicketNotFoundError';
  }
}

export class TenantValidationError extends TicketServiceError {
  constructor(message: string, details?: any) {
    super(message, 'TENANT_VALIDATION_ERROR', details);
    this.name = 'TenantValidationError';
  }
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

function validateCompanyContext(companyId: string): void {
  if (!companyId || companyId === 'default') {
    throw new TenantValidationError('Valid tenant ID required for ticket operations');
  }
}

function validateCreateTicketData(data: CreateTicketData): void {
  if (!data.title || data.title.trim().length === 0) {
    throw new TicketServiceError('Title is required', 'INVALID_TITLE');
  }

  if (!data.description || data.description.trim().length === 0) {
    throw new TicketServiceError('Description is required', 'INVALID_DESCRIPTION');
  }

  const validTypes: TicketType[] = [
    'equipment_failure',
    'preventive_maintenance',
    'inspection',
    'repair',
    'installation',
    'emergency',
    'safety_issue',
    'other'
  ];

  if (!validTypes.includes(data.type)) {
    throw new TicketServiceError(`Invalid ticket type: ${data.type}`, 'INVALID_TYPE');
  }

  const validPriorities: TicketPriority[] = ['critical', 'high', 'medium', 'low'];
  if (!validPriorities.includes(data.priority)) {
    throw new TicketServiceError(`Invalid priority: ${data.priority}`, 'INVALID_PRIORITY');
  }
}

async function generateTicketNumber(companyId: string): Promise<string> {
  const year = new Date().getFullYear();
  const ticketsRef = collection(db, 'companies', companyId, 'tickets');

  const yearStart = new Date(year, 0, 1);
  const q = query(
    ticketsRef,
    where('createdAt', '>=', Timestamp.fromDate(yearStart)),
    orderBy('createdAt', 'desc'),
    firestoreLimit(1)
  );

  const snapshot = await getDocs(q);
  let sequence = 1;

  if (!snapshot.empty) {
    const lastTicket = snapshot.docs[0].data();
    if (lastTicket.ticketNumber) {
      const match = lastTicket.ticketNumber.match(/TKT-\d{4}-(\d{5})/);
      if (match) {
        sequence = parseInt(match[1]) + 1;
      }
    }
  }

  return `TKT-${year}-${sequence.toString().padStart(5, '0')}`;
}

function calculateSLADeadlines(priority: TicketPriority, createdAt: Date) {
  const responseHours = {
    critical: 1,
    high: 4,
    medium: 24,
    low: 72
  };

  const resolutionHours = {
    critical: 4,
    high: 24,
    medium: 72,
    low: 168
  };

  const responseDeadline = new Date(createdAt);
  responseDeadline.setHours(responseDeadline.getHours() + responseHours[priority]);

  const resolutionDeadline = new Date(createdAt);
  resolutionDeadline.setHours(resolutionDeadline.getHours() + resolutionHours[priority]);

  return { responseDeadline, resolutionDeadline };
}

// ============================================================================
// CORE TICKET OPERATIONS
// ============================================================================

export async function createServiceTicket(
  ticketData: CreateTicketData,
  currentUserId: string,
  currentUserName?: string
): Promise<string> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);
    validateCreateTicketData(ticketData);

    const ticketNumber = await generateTicketNumber(companyId);
    const createdAt = new Date();
    const { responseDeadline, resolutionDeadline } = calculateSLADeadlines(
      ticketData.priority,
      createdAt
    );

    const ticket: Omit<ServiceTicket, 'id'> = {
      companyId,
      ticketNumber,
      title: ticketData.title.trim(),
      description: ticketData.description.trim(),
      type: ticketData.type,
      priority: ticketData.priority,
      status: ticketData.assignedTo ? 'assigned' : 'open',
      wellId: ticketData.wellId,
      wellName: ticketData.wellName,
      equipmentId: ticketData.equipmentId,
      equipmentName: ticketData.equipmentName,
      vendorId: ticketData.vendorId,
      vendorName: ticketData.vendorName,
      createdBy: currentUserId,
      createdByName: currentUserName,
      assignedTo: ticketData.assignedTo,
      assignedToName: ticketData.assignedToName,
      assignedAt: ticketData.assignedTo ? createdAt : undefined,
      createdAt,
      updatedAt: createdAt,
      scheduledDate: ticketData.scheduledDate,
      responseDeadline,
      resolutionDeadline,
      estimatedCost: ticketData.estimatedCost,
      safetyHazards: ticketData.safetyHazards || [],
      ppeRequired: ticketData.ppeRequired || [],
      permitsRequired: ticketData.permitsRequired || [],
      lockoutTagout: ticketData.lockoutTagout || false,
      notes: ticketData.notes,
      internalNotes: ticketData.internalNotes,
      tags: ticketData.tags || [],
      customFields: ticketData.customFields || {},
      photos: [],
      documents: []
    };

    const ticketsRef = collection(db, 'companies', companyId, 'tickets');
    const docRef = await addDoc(ticketsRef, ticket);

    return docRef.id;

  } catch (error: any) {
    console.error('❌ Error creating ticket:', error);

    if (error instanceof TicketServiceError) {
      throw error;
    }

    throw new TicketServiceError(
      'Failed to create ticket',
      'CREATE_FAILED',
      { error: error.message }
    );
  }
}

export async function getTicketById(
  ticketId: string,
  requestingCompanyId?: string
): Promise<ServiceTicket | null> {
  try {
    const companyId = requestingCompanyId || getCompanyId();
    validateCompanyContext(companyId);

    const ticketRef = doc(db, 'companies', companyId, 'tickets', ticketId);
    const ticketSnap = await getDoc(ticketRef);

    if (!ticketSnap.exists()) {
      return null;
    }

    const data = ticketSnap.data();

    if (data.companyId !== companyId) {
      return null;
    }

    return {
      id: ticketSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      scheduledDate: data.scheduledDate?.toDate(),
      assignedAt: data.assignedAt?.toDate(),
      startedAt: data.startedAt?.toDate(),
      completedAt: data.completedAt?.toDate(),
      closedAt: data.closedAt?.toDate(),
      responseDeadline: data.responseDeadline?.toDate(),
      resolutionDeadline: data.resolutionDeadline?.toDate()
    } as ServiceTicket;

  } catch (error: any) {
    console.error(`❌ Error fetching ticket ${ticketId}:`, error);
    throw new TicketServiceError(
      'Failed to fetch ticket',
      'FETCH_FAILED',
      { ticketId, error: error.message }
    );
  }
}

export async function getServiceTickets(
  options: TicketListOptions = {}
): Promise<ServiceTicket[]> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const ticketsRef = collection(db, 'companies', companyId, 'tickets');
    const constraints: QueryConstraint[] = [];

    constraints.push(where('companyId', '==', companyId));

    if (options.status) {
      if (Array.isArray(options.status)) {
        constraints.push(where('status', 'in', options.status));
      } else {
        constraints.push(where('status', '==', options.status));
      }
    } else if (!options.includeCompleted) {
      constraints.push(where('status', 'in', ['open', 'assigned', 'in_progress', 'waiting_parts']));
    }

    if (options.priority) {
      if (Array.isArray(options.priority)) {
        constraints.push(where('priority', 'in', options.priority));
      } else {
        constraints.push(where('priority', '==', options.priority));
      }
    }

    if (options.type) {
      constraints.push(where('type', '==', options.type));
    }

    if (options.wellId) {
      constraints.push(where('wellId', '==', options.wellId));
    }

    if (options.assignedTo) {
      constraints.push(where('assignedTo', '==', options.assignedTo));
    }

    if (options.createdBy) {
      constraints.push(where('createdBy', '==', options.createdBy));
    }

    if (options.vendorId) {
      constraints.push(where('vendorId', '==', options.vendorId));
    }

    const orderField = options.orderByField || 'createdAt';
    const orderDir = options.orderDirection || 'desc';
    constraints.push(orderBy(orderField, orderDir));

    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(ticketsRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        scheduledDate: data.scheduledDate?.toDate(),
        assignedAt: data.assignedAt?.toDate(),
        startedAt: data.startedAt?.toDate(),
        completedAt: data.completedAt?.toDate(),
        closedAt: data.closedAt?.toDate(),
        responseDeadline: data.responseDeadline?.toDate(),
        resolutionDeadline: data.resolutionDeadline?.toDate()
      } as ServiceTicket;
    });

  } catch (error: any) {
    console.error('❌ Error fetching tickets:', error);
    throw new TicketServiceError(
      'Failed to fetch tickets',
      'FETCH_TICKETS_FAILED',
      { error: error.message }
    );
  }
}

export async function updateServiceTicket(
  ticketId: string,
  updateData: UpdateTicketData,
  _updatingUserId?: string // Prefixed with _ to indicate intentionally unused (reserved for future audit trail)
): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const existingTicket = await getTicketById(ticketId, companyId);
    if (!existingTicket) {
      throw new TicketNotFoundError(ticketId);
    }

    if (existingTicket.companyId !== companyId) {
      throw new TenantValidationError('Cannot update ticket from different tenant');
    }

    const updateDoc: any = {
      ...updateData,
      updatedAt: Timestamp.now()
    };

    if (updateData.status) {
      if (updateData.status === 'in_progress' && !existingTicket.startedAt) {
        updateDoc.startedAt = Timestamp.now();
      }
      if (updateData.status === 'resolved' && !existingTicket.completedAt) {
        updateDoc.completedAt = Timestamp.now();
      }
      if (updateData.status === 'closed' && !existingTicket.closedAt) {
        updateDoc.closedAt = Timestamp.now();
      }
    }

    if (updateData.assignedTo && updateData.assignedTo !== existingTicket.assignedTo) {
      updateDoc.assignedAt = Timestamp.now();
      if (existingTicket.status === 'open') {
        updateDoc.status = 'assigned';
      }
    }

    const now = new Date();
    if (existingTicket.resolutionDeadline && now > existingTicket.resolutionDeadline) {
      updateDoc.slaBreached = true;
    }

    delete updateDoc.companyId;
    delete updateDoc.companyId;
    delete updateDoc.ticketNumber;
    delete updateDoc.id;

    const ticketRef = doc(db, 'companies', companyId, 'tickets', ticketId);
    await updateDoc(ticketRef, updateDoc);

    return true;

  } catch (error: any) {
    console.error(`❌ Error updating ticket ${ticketId}:`, error);

    if (error instanceof TicketServiceError) {
      throw error;
    }

    throw new TicketServiceError(
      'Failed to update ticket',
      'UPDATE_FAILED',
      { ticketId, error: error.message }
    );
  }
}

export async function deleteServiceTicket(
  ticketId: string,
  deletingUserId: string,
  hardDelete: boolean = false
): Promise<boolean> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const existingTicket = await getTicketById(ticketId, companyId);
    if (!existingTicket) {
      throw new TicketNotFoundError(ticketId);
    }

    if (existingTicket.companyId !== companyId) {
      throw new TenantValidationError('Cannot delete ticket from different tenant');
    }

    const ticketRef = doc(db, 'companies', companyId, 'tickets', ticketId);

    if (hardDelete) {
      await deleteDoc(ticketRef);
    } else {
      await updateDoc(ticketRef, {
        status: 'cancelled',
        closedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }

    return true;

  } catch (error: any) {
    console.error(`❌ Error deleting ticket ${ticketId}:`, error);

    if (error instanceof TicketServiceError) {
      throw error;
    }

    throw new TicketServiceError(
      'Failed to delete ticket',
      'DELETE_FAILED',
      { ticketId, error: error.message }
    );
  }
}

// ============================================================================
// REAL-TIME SUBSCRIPTION METHODS (Enterprise SaaS Best Practice)
// ============================================================================

/**
 * Subscribe to real-time ticket updates for current tenant
 * Returns unsubscribe function for cleanup
 * 
 * @param callback - Function called with updated tickets array
 * @param options - Optional query filters
 * @returns Unsubscribe function
 * 
 * @example
 * useEffect(() => {
 *   const unsubscribe = subscribeToTickets((tickets) => {
 *     setTickets(tickets);
 *   });
 *   return () => unsubscribe();
 * }, []);
 */
export function subscribeToTickets(
  callback: (tickets: ServiceTicket[]) => void,
  options?: {
    status?: TicketStatus | TicketStatus[];
    priority?: TicketPriority | TicketPriority[];
    wellId?: string;
    assignedTo?: string;
    limit?: number;
  }
): Unsubscribe {
  const companyId = getCompanyId();

  if (!companyId || companyId === 'default') {
    return () => { }; // Return no-op cleanup
  }

  try {

    // Build query with tenant isolation
    const ticketsRef = collection(db, 'companies', companyId, 'tickets');
    const constraints: QueryConstraint[] = [
      where('companyId', '==', companyId),
      where('deleted', '==', false)
    ];

    // Apply optional filters
    if (options?.status) {
      if (Array.isArray(options.status)) {
        constraints.push(where('status', 'in', options.status));
      } else {
        constraints.push(where('status', '==', options.status));
      }
    }

    if (options?.priority) {
      if (Array.isArray(options.priority)) {
        constraints.push(where('priority', 'in', options.priority));
      } else {
        constraints.push(where('priority', '==', options.priority));
      }
    }

    if (options?.wellId) {
      constraints.push(where('wellId', '==', options.wellId));
    }

    if (options?.assignedTo) {
      constraints.push(where('assignedTo', '==', options.assignedTo));
    }

    // Order by most recent first
    constraints.push(orderBy('createdAt', 'desc'));

    // Apply limit if specified
    if (options?.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(ticketsRef, ...constraints);

    // Setup real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const tickets: ServiceTicket[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          tickets.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            scheduledDate: data.scheduledDate?.toDate(),
            completedAt: data.completedAt?.toDate(),
          } as ServiceTicket);
        });

        callback(tickets);
      },
      (error) => {
        console.error('❌ Error in ticket subscription:', error);
        // Call callback with empty array on error to prevent UI from hanging
        callback([]);
      }
    );

    return unsubscribe;

  } catch (error) {
    console.error('❌ Error setting up ticket subscription:', error);
    return () => { }; // Return no-op cleanup
  }
}

/**
 * Subscribe to real-time vendor updates for current tenant
 * Returns unsubscribe function for cleanup
 * 
 * @param callback - Function called with updated vendors array
 * @param options - Optional query filters
 * @returns Unsubscribe function
 * 
 * @example
 * useEffect(() => {
 *   const unsubscribe = subscribeToVendors((vendors) => {
 *     setVendors(vendors);
 *   });
 *   return () => unsubscribe();
 * }, []);
 */
export function subscribeToVendors(
  callback: (vendors: Vendor[]) => void,
  options?: {
    status?: 'active' | 'inactive' | 'pending_approval' | 'suspended' | 'rejected';
    services?: string[];
    limit?: number;
  }
): Unsubscribe {
  const companyId = getCompanyId();

  if (!companyId || companyId === 'default') {
    return () => { }; // Return no-op cleanup
  }

  try {

    // Build query with tenant isolation
    const vendorsRef = collection(db, 'companies', companyId, 'vendors');
    const constraints: QueryConstraint[] = [
      where('companyId', '==', companyId),
      where('deleted', '==', false)
    ];

    // Apply optional filters
    if (options?.status) {
      constraints.push(where('status', '==', options.status));
    }

    if (options?.services && options.services.length > 0) {
      // Firestore array-contains-any for service matching
      constraints.push(where('services', 'array-contains-any', options.services));
    }

    // Order by name
    constraints.push(orderBy('name', 'asc'));

    // Apply limit if specified
    if (options?.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(vendorsRef, ...constraints);

    // Setup real-time listener
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const vendors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Vendor[];
      callback(vendors);
    });

    return unsubscribe;

  } catch (error) {
    console.error('❌ Error in vendor subscription:', error);
    return () => { };
  }
}

// ============================================================================
// REVIEW & RATING LOGIC
// ============================================================================

/**
 * Submits a review for a service ticket.
 * This drives the dynamic vendor analytics without requiring a separate "Reviews" collection.
 */
export async function submitTicketReview(
  ticketId: string,
  reviewData: {
    score: number;
    tags: string[];
    comment?: string;
    categories?: TicketRating['categories'];
  },
  userId: string
): Promise<void> {
  try {
    const companyId = getCompanyId();
    const ticketRef = doc(db, 'companies', companyId, 'tickets', ticketId);

    // Construct the rating object
    const rating: TicketRating = {
      ...reviewData,
      ratedBy: userId,
      ratedAt: new Date(),
      isAutoRated: false
    };

    await updateDoc(ticketRef, {
      review: rating,
      reviewStatus: 'rated',
      updatedAt: Timestamp.now()
    });

  } catch (error) {
    console.error('Error submitting review:', error);
    throw error;
  }
}

/**
 * Automatically rates a ticket as 5-star (Satisfactory) if no complaints were lodged.
 * Solves "Pumper Fatigue" by assuming good service unless flagged.
 */
export async function autoRateTicket(
  ticketId: string,
  systemUserId: string = 'system'
): Promise<void> {
  try {
    const companyId = getCompanyId();
    const ticketRef = doc(db, 'companies', companyId, 'tickets', ticketId);

    const rating: TicketRating = {
      score: 5,
      tags: ['Auto-Rated', 'Standard Service'],
      comment: 'Auto-rated based on no complaints.',
      ratedBy: systemUserId,
      ratedAt: new Date(),
      isAutoRated: true,
      categories: {
        cleanliness: 5,
        timeliness: 5,
        quality: 5,
        safety: 5
      }
    };

    await updateDoc(ticketRef, {
      review: rating,
      reviewStatus: 'auto_rated',
      updatedAt: Timestamp.now()
    });

  } catch (error) {
    console.error('Error auto-rating ticket:', error);
    throw error;
  }
}



/**
 * Get open tickets (not closed/resolved/cancelled)
 */
export async function getOpenTickets(): Promise<ServiceTicket[]> {
  return getServiceTickets({
    status: ['open', 'assigned', 'in_progress', 'waiting_parts'],
    includeCompleted: false
  });
}

// Export service object as named export
export const serviceTicketService = {
  createServiceTicket,
  getTicketById,
  getServiceTickets,
  getOpenTickets,
  updateServiceTicket,
  deleteServiceTicket,
  subscribeToTickets,
  subscribeToVendors
};

// Also export as default for compatibility
export default serviceTicketService;
